import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import TreeView, {flattenTree} from 'react-accessible-treeview';
import { FaFileDownload, FaRegFolder, FaRegFolderOpen, FaInfoCircle } from "react-icons/fa";
import './FilesView.css'
import { convertToNestedStructure, searchTree } from '../../utils/common-utils';

const FolderIcon = React.memo(({ isOpen }) =>
  isOpen ? (
    <FaRegFolderOpen className="icon"/>
  ) : (
    <FaRegFolder className="icon" />
  )
);

// Tooltip component that only shows when content is truncated
export const ConditionalTooltip = React.memo(({ content, children }) => {
  const elementRef = useRef(null);
  const [isTextTruncated, setIsTextTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (elementRef.current) {
        const { scrollWidth, clientWidth } = elementRef.current;
        setIsTextTruncated(scrollWidth > clientWidth);
      }
    };

    checkTruncation();
    // Add resize listener to recheck truncation
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [content]);

  const childrenWithRef = React.Children.map(children, child =>
    React.cloneElement(child, { ref: elementRef })
  );

  if (!isTextTruncated) {
    return childrenWithRef;
  }

  return (
    <div className="tooltip-container">
      {childrenWithRef}
      <span className="tooltip-text">{content}</span>
    </div>
  );
});

// Info badge component
const InfoBadge = React.memo(({ count, type }) => (
  <span className="info-badge" title={`Contains ${count} ${type}`}>
    {count}
  </span>
));

// Debounce helper function
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const FilesView = ({ files, fileKey}) => {
  const [query, setQuery] = useState('');
  const [isSearchData, setIsSearchData] = useState(true);
  const [expandedIds, setExpandedIds] = useState([]);
  
  // Debounce the search query
  const debouncedQuery = useDebounce(query, 300);

  // Memoize the initial nested structure
  const initialData = useMemo(() => convertToNestedStructure(files, fileKey), [files, fileKey]);
  
  // Memoize the search results
  const searchResults = useMemo(() => {
    if (!debouncedQuery) return null;
    return searchTree(initialData, debouncedQuery);
  }, [initialData, debouncedQuery]);
  
  // Memoize the flattened tree data
  const treeData = useMemo(() => {
    if (!debouncedQuery) {
      return flattenTree(initialData);
    }
    if (searchResults && searchResults.length > 0) {
      return flattenTree(searchResults[0]);
    }
    return flattenTree(initialData);
  }, [initialData, debouncedQuery, searchResults]);

  // Update search status when results change
  useEffect(() => {
    if (debouncedQuery && (!searchResults || searchResults.length === 0)) {
      setIsSearchData(false);
    } else {
      setIsSearchData(true);
    }
  }, [debouncedQuery, searchResults]);

  // Memoize expanded IDs calculation
  const currentExpandedIds = useMemo(() => {
    if (debouncedQuery) {
      return treeData.map(node => node.id);
    }
    return expandedIds;
  }, [debouncedQuery, treeData, expandedIds]);

  const handleSearch = useCallback((e) => {
    const searchQuery = e.target.value;
    setQuery(searchQuery);
    if (!searchQuery) {
      setExpandedIds([]);
      setIsSearchData(true);
    }
  }, []);

  // Calculate folder contents for badges
  const getFolderContents = useCallback((element) => {
    if (!element.children) return { files: 0, folders: 0 };
    return element.children.reduce((acc, child) => ({
      files: acc.files + (child.children ? 0 : 1),
      folders: acc.folders + (child.children ? 1 : 0)
    }), { files: 0, folders: 0 });
  }, []);

  // Enhanced node renderer with conditional tooltips
  const nodeRenderer = useCallback(({
    element,
    isBranch,
    isExpanded,
    getNodeProps,
    level,
    handleExpand,
  }) => {
    const nodeProps = getNodeProps({ onClick: handleExpand });
    const folderContents = isBranch ? getFolderContents(element) : null;
    
    return (
      <div {...nodeProps} style={{ paddingLeft: 20 * (level - 1) }} className="tree-node-content">
        {isBranch ? (
          <div className="node-with-badges">
            <div className="node-main-content">
              <FolderIcon isOpen={isExpanded} />
              <span className="node-name">{element.name}</span>
            </div>
            {!isExpanded && (
              <div className="badge-container">
                {folderContents.folders > 0 && (
                  <InfoBadge count={folderContents.folders} type="folders" />
                )}
                {folderContents.files > 0 && (
                  <InfoBadge count={folderContents.files} type="files" />
                )}
              </div>
            )}
          </div>
        ) : (
          <ConditionalTooltip content={element.name}>
            <span className="node-name">{element.name}</span>
          </ConditionalTooltip>
        )}
      </div>
    );
  }, [fileKey, getFolderContents]);

  return (
    <div>
      <input
        type="text"
        placeholder="Search files and folders"
        value={query}
        onChange={handleSearch}
        className='search-input-dir'
      />
      <div className="directory">
        {isSearchData ? (
          <TreeView
            key={debouncedQuery}
            data={treeData}
            aria-label="directory tree"
            multiSelect
            propagateSelect
            propagateSelectUpwards
            togglableSelect
            expandedIds={currentExpandedIds}
            nodeRenderer={nodeRenderer}
          />
        ) : (
          <div>No files found</div>
        )}
      </div>
    </div>
  );
};

export default React.memo(FilesView);
